import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createElement, useEffect, useRef, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import { getNavigationPath } from "@/common/components/notifications/NotificationItem";
import NotificationToast from "@/common/components/notifications/NotificationToast";
import type {
  Notification,
  NotificationPage,
} from "@/common/models/notification.model";
import EnvConfig from "@/config/env";

// ---------------------------------------------------------------------------
// Query keys — centralised so invalidations are consistent
// ---------------------------------------------------------------------------

export const NOTIF_KEYS = {
  list: (page: number) => ["notifications", "list", page] as const,
  unreadCount: () => ["notifications", "unreadCount"] as const,
};

const PAGE_SIZE = 25;

function prependNotificationToPage(
  oldPage: NotificationPage | undefined,
  notification: Notification,
): NotificationPage | undefined {
  if (!oldPage?.content) return oldPage;
  if (oldPage.content.some((n) => n.id === notification.id)) return oldPage;
  return {
    ...oldPage,
    content: [notification, ...oldPage.content].slice(
      0,
      oldPage.size ?? PAGE_SIZE,
    ),
    totalElements: (oldPage.totalElements ?? oldPage.content.length) + 1,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

// Module-level guard: the hook is mounted by several components at once
// (icon rail badge, sidebar, notifications page). Only ONE of them may own
// the app-wide SSE connection — the rest rely on the shared react-query cache
// (the owner writes incoming notifications into it) and the polling fallback.
let sseConnectionHeld = false;

export function useNotifications() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // SSE refs — stable across renders
  const eventSourceRef = useRef<EventSource | null>(null);
  const isSseOwnerRef = useRef(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const reconnectAttempts = useRef(0);
  // Timer that only resets the backoff counter once the stream has stayed open
  // for a while — see connectSse for why a flapping connection must NOT reset
  // its backoff on every `connected` event.
  const stableConnTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_RECONNECT = 5;
  const POLL_INTERVAL_MS = 60_000; // fallback polling when SSE is dead

  // Pagination state
  const [page, setPage] = useState(0);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [hasMore, setHasMore] = useState(false);

  // ---------------------------------------------------------------------------
  // REST queries
  // ---------------------------------------------------------------------------

  const { data: notifPage, isLoading: isLoadingList } =
    useQuery<NotificationPage>({
      queryKey: NOTIF_KEYS.list(page),
      queryFn: (): Promise<NotificationPage> =>
        httpClient<NotificationPage>(
          httpMethods.GET,
          API_ENDPOINTS.NOTIFICATIONS.list,
          {
            page,
            size: PAGE_SIZE,
            unreadOnly: false,
          },
        ).then((r) => r.data),
      placeholderData: (prev) => prev,
      staleTime: 10_000,
    });

  // Merge newly fetched page into the accumulated list
  useEffect(() => {
    if (!notifPage) return;
    const incoming = notifPage.content ?? [];
    setAllNotifications((prev) => {
      if (page === 0) return incoming; // reset on first page
      // Append, deduplicating by id
      const existingIds = new Set(prev.map((n) => n.id));
      return [...prev, ...incoming.filter((n) => !existingIds.has(n.id))];
    });
    setHasMore(notifPage.number < notifPage.totalPages - 1);
  }, [notifPage, page]);

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: NOTIF_KEYS.unreadCount(),
    queryFn: () =>
      httpClient<{ count: number }>(
        httpMethods.GET,
        API_ENDPOINTS.NOTIFICATIONS.unreadCount,
        undefined,
        {
          headers: { "X-Background-Request": "true" }, // background poll — does not extend session
        },
      ).then((r) => r.data),
    staleTime: 10_000,
    refetchInterval: POLL_INTERVAL_MS, // keeps badge accurate when SSE is offline
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const markReadMutation = useMutation({
    mutationFn: (id: number) =>
      httpClient<void>(
        httpMethods.PATCH,
        API_ENDPOINTS.NOTIFICATIONS.markRead(id),
      ),
    onSuccess: (_, id) => {
      // Optimistically update in local list
      setAllNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
      );
      // Keep cached paged lists in sync so unread state doesn't reappear after navigation/remount.
      queryClient.setQueriesData<NotificationPage>(
        { queryKey: ["notifications", "list"] },
        (old) => {
          if (!old?.content) return old;
          return {
            ...old,
            content: old.content.map((n) =>
              n.id === id ? { ...n, read: true } : n,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: NOTIF_KEYS.unreadCount() });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () =>
      httpClient<void>(
        httpMethods.PATCH,
        API_ENDPOINTS.NOTIFICATIONS.markAllRead,
      ),
    onSuccess: () => {
      setAllNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      queryClient.setQueriesData<NotificationPage>(
        { queryKey: ["notifications", "list"] },
        (old) => {
          if (!old?.content) return old;
          return {
            ...old,
            content: old.content.map((n) => ({ ...n, read: true })),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: NOTIF_KEYS.unreadCount() });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: () =>
      httpClient<void>(
        httpMethods.DELETE,
        API_ENDPOINTS.NOTIFICATIONS.clearAll,
      ),
    onSuccess: () => {
      setAllNotifications([]);
      queryClient.setQueriesData<NotificationPage>(
        { queryKey: ["notifications", "list"] },
        (old) => {
          if (!old) return old;
          return { ...old, content: [], totalElements: 0 };
        },
      );
      queryClient.invalidateQueries({ queryKey: NOTIF_KEYS.unreadCount() });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      httpClient<void>(
        httpMethods.DELETE,
        API_ENDPOINTS.NOTIFICATIONS.deleteById(id),
      ),
    onSuccess: (_, id) => {
      setAllNotifications((prev) => prev.filter((n) => n.id !== id));
      queryClient.setQueriesData<NotificationPage>(
        { queryKey: ["notifications", "list"] },
        (old) => {
          if (!old?.content) return old;
          return {
            ...old,
            content: old.content.filter((n) => n.id !== id),
            totalElements: Math.max(
              0,
              (old.totalElements ?? old.content.length) - 1,
            ),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: NOTIF_KEYS.unreadCount() });
    },
  });

  // ---------------------------------------------------------------------------
  // Toast click → mark read + navigate to the related entity
  // ---------------------------------------------------------------------------

  // Kept behind a ref so the SSE listener (created inside connectSse) can call
  // the latest handler without forcing connectSse to be recreated on every
  // navigate/mutation identity change (which would churn the SSE connection).
  const handleToastClick = useCallback(
    (n: Notification) => {
      if (!n.read) markReadMutation.mutate(n.id);
      const path = getNavigationPath(n);
      navigate(path ?? "/notifications");
    },
    [navigate, markReadMutation],
  );
  const handleToastClickRef = useRef(handleToastClick);
  useEffect(() => {
    handleToastClickRef.current = handleToastClick;
  }, [handleToastClick]);

  // ---------------------------------------------------------------------------
  // SSE connection
  // ---------------------------------------------------------------------------

  const connectSse = useCallback(() => {
    // Another hook instance already owns the app-wide connection
    if (!isSseOwnerRef.current && sseConnectionHeld) return;
    // Already connected
    if (eventSourceRef.current?.readyState === EventSource.OPEN) return;

    const url = `${EnvConfig.serverURL}${API_ENDPOINTS.NOTIFICATIONS.sseStream}`;

    try {
      const es = new EventSource(url, { withCredentials: true });
      isSseOwnerRef.current = true;
      sseConnectionHeld = true;
      eventSourceRef.current = es;

      es.addEventListener("connected", () => {
        // Do NOT reset the backoff counter immediately. A connection that drops
        // a second or two after opening (transport/proxy flap) would otherwise
        // reconnect every ~1–2s forever — never reaching MAX_RECONNECT — and
        // churn the browser's ~6-connections-per-host pool, starving axios
        // requests on SSE-heavy pages (e.g. the cycle runner). Only treat the
        // stream as healthy once it has stayed open for 30s.
        if (stableConnTimerRef.current)
          clearTimeout(stableConnTimerRef.current);
        stableConnTimerRef.current = setTimeout(() => {
          reconnectAttempts.current = 0;
        }, 30_000);
      });

      es.addEventListener("notification", (event) => {
        try {
          const notification: Notification = JSON.parse(event.data);

          // Prepend to local list so newest appears at top
          setAllNotifications((prev) => {
            if (prev.some((n) => n.id === notification.id)) return prev;
            return [notification, ...prev];
          });

          queryClient.setQueryData<NotificationPage>(
            NOTIF_KEYS.list(0),
            (old) => prependNotificationToPage(old, notification),
          );

          // Bump unread count cache
          queryClient.setQueryData<{ count: number }>(
            NOTIF_KEYS.unreadCount(),
            (old) => ({
              count: (old?.count ?? 0) + 1,
            }),
          );

          // Show a clickable toast so the user notices even if the panel is
          // closed — clicking it marks the notification read and navigates to
          // the related entity (defect, run, cycle, …).
          toast.custom(
            (id) =>
              createElement(NotificationToast, {
                notification,
                onClick: () => {
                  handleToastClickRef.current(notification);
                  toast.dismiss(id);
                },
                onDismiss: () => toast.dismiss(id),
              }),
            {
              duration: 6000,
              // Neutralise the global Toaster container styling so the custom
              // card fully controls its own look (and respects dark mode).
              style: {
                padding: 0,
                background: "transparent",
                border: "none",
                boxShadow: "none",
              },
            },
          );
        } catch {
          console.warn("[NotifSSE] Failed to parse notification event");
        }
      });

      es.onerror = () => {
        console.warn("[NotifSSE] ❌ Connection error");
        // A drop before the 30s stability window must not count as healthy.
        if (stableConnTimerRef.current) {
          clearTimeout(stableConnTimerRef.current);
          stableConnTimerRef.current = null;
        }
        es.close();
        eventSourceRef.current = null;

        if (reconnectAttempts.current < MAX_RECONNECT) {
          // Exponential backoff with a 3s floor / 30s ceiling so a flapping
          // stream can't hammer reconnects (which churns the HTTP/1.1
          // connection pool and starves other requests on the page).
          const delay = Math.min(
            3000 * Math.pow(2, reconnectAttempts.current),
            30_000,
          );
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connectSse, delay);
        } else {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current = 0;
            connectSse();
          }, 5 * 60_000);
        }
      };
    } catch (e) {
      console.warn("[NotifSSE] Could not open SSE connection:", e);
    }
  }, [queryClient]);

  // Open SSE on mount, close on unmount
  useEffect(() => {
    connectSse();
    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (reconnectTimeoutRef.current)
        clearTimeout(reconnectTimeoutRef.current);
      if (stableConnTimerRef.current) clearTimeout(stableConnTimerRef.current);
      // Release the app-wide connection slot so a later mount can reconnect.
      if (isSseOwnerRef.current) {
        isSseOwnerRef.current = false;
        sseConnectionHeld = false;
      }
    };
  }, [connectSse]);

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  return {
    // Data
    notifications: allNotifications,
    unreadCount: unreadData?.count ?? 0,
    isLoadingList,
    hasMore,

    // Actions
    loadMore: () => setPage((p) => p + 1),
    markRead: (id: number) => markReadMutation.mutate(id),
    markAllRead: () => markAllReadMutation.mutate(),
    deleteNotification: (id: number) => deleteMutation.mutate(id),
    clearAll: () => clearAllMutation.mutate(),
  };
}
